import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Check, Pencil, ToggleLeft, ToggleRight, X } from 'lucide-react'
import Spinner from './Spinner'
import { useUpdateProjectMutation } from '../services/projectDataApi'
import { PolishPassesSelector } from './qaConfig'
import { bugCategoryLabel } from '../../netlify/functions/lib/polish-pass-types'

// Inline editors for the rows in ProjectSettings. Every one writes through PATCH /api/projects/:id —
// the same endpoint the chat's update_project_settings tool calls — so a change made here and one made
// in conversation are the same write, with the same server-side clamping and the same side effects
// (requeueing journeys, refreshing the share card). Nothing
// here validates on its own account: the row states the accepted range for the person typing, and the
// server remains the authority.
//
// Settings whose value isn't something a person can just type — the GitHub connection, the issue
// tracker, members, saved logins — are NOT here. They carry secrets, multi-step auth, or their own
// side effects, and stay with the chat; see ProjectSettings.

// An error message to show against the row, or null when the write landed.
export type PatchResult = string | null
export type Patch = (data: Record<string, unknown>) => Promise<PatchResult>

export type Tone = 'ok' | 'muted' | 'accent'

// The current value of a setting, stated the same way in every row.
export function Chip({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`settings-row__chip settings-row__chip--${tone}`}>{children}</span>
}

function patchErrorMessage(err: unknown): string {
  // The functions answer failures as { error: string }; anything else (network, a proxy) gets the
  // generic line rather than "[object Object]".
  const data = (err as { data?: unknown } | undefined)?.data
  const message = data && typeof data === 'object' ? (data as { error?: unknown }).error : undefined
  return typeof message === 'string' && message.trim() ? message : 'Could not save that change. Try again.'
}

// One writer for every row, so a row stays presentational and a failure reads the same everywhere.
// Callers pass the body they want: a bare field patch (folded into `update-settings` by the mutation)
// or an explicit `action`, e.g. update-status.
export function useProjectPatch(projectId: string): Patch {
  const [updateProject] = useUpdateProjectMutation()
  return useCallback(async (data) => {
    try {
      await updateProject({ id: projectId, data }).unwrap()
      return null
    } catch (err) {
      return patchErrorMessage(err)
    }
  }, [projectId, updateProject])
}

// The shared row frame: the label/value line, with the open editor (or a failure) beneath it. Editable
// rows always stack, open or not, so opening one doesn't relayout the line above it.
export function RowShell({ label, detail, right, below, testId }: {
  label: string
  detail?: string
  right: ReactNode
  below?: ReactNode
  testId?: string
}) {
  return (
    <div className="settings-row settings-row--editable" data-testid={testId ?? 'settings-row'}>
      <div className="settings-row__head">
        <div className="settings-row__info">
          <span className="settings-row__label">{label}</span>
          {detail && <span className="settings-row__detail">{detail}</span>}
        </div>
        <div className="settings-row__status">{right}</div>
      </div>
      {below}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return <p className="settings-row__error" role="alert">{children}</p>
}

function EditorActions({ saving, onCancel }: { saving: boolean; onCancel: () => void }) {
  return (
    <div className="settings-row__editor-actions">
      <button type="submit" className="settings-row__save" disabled={saving} data-testid="settings-row-save" data-analytics-event="project-settings-save">
        {saving ? <Spinner size={12} /> : <Check size={13} aria-hidden />} Save
      </button>
      <button type="button" className="settings-row__cancel" onClick={onCancel} disabled={saving} data-testid="settings-row-cancel" data-analytics-event="project-settings-cancel">
        Cancel
      </button>
    </div>
  )
}

// A setting that is simply on or off. No editor to open and no Save: the button flips it, because
// there is nothing to review before committing a two-state value.
export function ToggleRow({ label, detail, chip, on, enableLabel, disableLabel, canEdit, patch, testId }: {
  label: string
  detail?: string
  chip: ReactNode
  on: boolean
  enableLabel: string
  disableLabel: string
  canEdit: boolean
  patch: (next: boolean) => Promise<PatchResult>
  testId?: string
}) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function flip() {
    setSaving(true)
    setError(null)
    const failure = await patch(!on)
    setSaving(false)
    setError(failure)
  }

  return (
    <RowShell
      label={label}
      detail={detail}
      testId={testId}
      right={
        <>
          {chip}
          {canEdit && (
            <button
              type="button"
              className="settings-row__change"
              onClick={() => void flip()}
              disabled={saving}
              data-testid="settings-row-toggle"
              data-analytics-event="project-settings-toggle"
            >
              {saving ? <Spinner size={12} /> : on ? <ToggleRight size={13} aria-hidden /> : <ToggleLeft size={13} aria-hidden />}
              {on ? disableLabel : enableLabel}
            </button>
          )}
        </>
      }
      below={error && <ErrorNote>{error}</ErrorNote>}
    />
  )
}

// Shared open/close + save plumbing for the rows that DO need a Save (a typed value is worth a
// deliberate commit, and an accidental keystroke shouldn't reconfigure a running project).
function useRowEditor<T>(patch: (value: T) => Promise<PatchResult>) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const close = useCallback(() => {
    setOpen(false)
    setError(null)
  }, [])

  const submit = useCallback(async (value: T) => {
    setSaving(true)
    setError(null)
    const failure = await patch(value)
    setSaving(false)
    if (failure) setError(failure)
    else setOpen(false)
  }, [patch])

  return { open, setOpen, saving, error, setError, close, submit }
}

function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="settings-row__change"
      onClick={onClick}
      data-testid="settings-row-edit"
      data-analytics-event="project-settings-edit"
    >
      <Pencil size={13} aria-hidden /> Edit
    </button>
  )
}

// A whole number, optionally with an "unlimited" state that writes null (the budget's no-cap value).
export function NumberRow({
  label, detail, chip, value, min, max, step = 1, unit, unlimitedLabel, canEdit, patch, testId,
}: {
  label: string
  detail?: string
  chip: ReactNode
  // null means unlimited, which is only offered when unlimitedLabel is given.
  value: number | null
  min: number
  max: number
  step?: number
  unit?: string
  unlimitedLabel?: string
  canEdit: boolean
  patch: (next: number | null) => Promise<PatchResult>
  testId?: string
}) {
  const editor = useRowEditor(patch)
  const [draft, setDraft] = useState('')
  const [unlimited, setUnlimited] = useState(false)

  // Seed from the project on open rather than syncing every render, so a background refetch can't
  // overwrite half-typed input.
  function startEditing() {
    setDraft(String(value ?? min))
    setUnlimited(value == null)
    editor.setOpen(true)
  }

  function save() {
    if (unlimited) {
      void editor.submit(null)
      return
    }
    const parsed = Number(draft)
    // Number('') is 0, so an empty field would otherwise save a real zero.
    if (!draft.trim() || !Number.isInteger(parsed)) {
      editor.setError('Enter a whole number.')
      return
    }
    if (parsed < min || parsed > max) {
      editor.setError(`Enter a number between ${min.toLocaleString()} and ${max.toLocaleString()}.`)
      return
    }
    void editor.submit(parsed)
  }

  return (
    <RowShell
      label={label}
      detail={detail}
      testId={testId}
      right={<>{chip}{canEdit && !editor.open && <EditButton onClick={startEditing} />}</>}
      below={editor.open && (
        <form
          className="settings-row__editor"
          // The min/max on the input are for the stepper and the hint; without noValidate the browser
          // silently blocks the submit and shows its own bubble instead of the row's message.
          noValidate
          onSubmit={(e) => { e.preventDefault(); save() }}
        >
          <div className="settings-row__editor-field">
            <input
              type="number"
              className="settings-row__input settings-row__input--number"
              value={draft}
              min={min}
              max={max}
              step={step}
              disabled={unlimited || editor.saving}
              onChange={(e) => setDraft(e.target.value)}
              aria-label={label}
              autoFocus
              data-testid="settings-row-number"
            />
            {unit && <span className="settings-row__unit">{unit}</span>}
            {unlimitedLabel && (
              <label className="settings-row__checkbox">
                <input
                  type="checkbox"
                  checked={unlimited}
                  disabled={editor.saving}
                  onChange={(e) => setUnlimited(e.target.checked)}
                  data-testid="settings-row-unlimited"
                />
                {unlimitedLabel}
              </label>
            )}
          </div>
          <span className="settings-row__hint">
            {min.toLocaleString()}–{max.toLocaleString()}
            {unlimitedLabel ? '. Or remove the cap entirely.' : '.'}
          </span>
          <EditorActions saving={editor.saving} onCancel={editor.close} />
          {editor.error && <ErrorNote>{editor.error}</ErrorNote>}
        </form>
      )}
    />
  )
}

// A free-text setting: a URL on one line, or a document in a textarea. An empty value clears the
// field server-side when nullable (for example, a design document); required URLs reject clearing.
export function TextRow({
  label, detail, chip, value, placeholder, multiline, hint, canEdit, patch, testId,
}: {
  label: string
  detail?: string
  chip: ReactNode
  value: string | null | undefined
  placeholder?: string
  multiline?: boolean
  hint?: string
  canEdit: boolean
  patch: (next: string) => Promise<PatchResult>
  testId?: string
}) {
  const editor = useRowEditor(patch)
  const [draft, setDraft] = useState('')

  function startEditing() {
    setDraft(value ?? '')
    editor.setOpen(true)
  }

  return (
    <RowShell
      label={label}
      detail={detail}
      testId={testId}
      right={<>{chip}{canEdit && !editor.open && <EditButton onClick={startEditing} />}</>}
      below={editor.open && (
        <form
          className="settings-row__editor"
          onSubmit={(e) => { e.preventDefault(); void editor.submit(draft.trim()) }}
        >
          {multiline ? (
            <textarea
              className="settings-row__textarea"
              value={draft}
              rows={8}
              placeholder={placeholder}
              disabled={editor.saving}
              onChange={(e) => setDraft(e.target.value)}
              aria-label={label}
              autoFocus
              data-testid="settings-row-text"
            />
          ) : (
            <input
              type="text"
              className="settings-row__input"
              value={draft}
              placeholder={placeholder}
              disabled={editor.saving}
              onChange={(e) => setDraft(e.target.value)}
              aria-label={label}
              autoFocus
              data-testid="settings-row-text"
            />
          )}
          {hint && <span className="settings-row__hint">{hint}</span>}
          <EditorActions saving={editor.saving} onCancel={editor.close} />
          {editor.error && <ErrorNote>{editor.error}</ErrorNote>}
        </form>
      )}
    />
  )
}

// A row whose editor is a control of its own (the polish-pass picker), which writes as the user
// clicks. There is no Save here — the child owns its writes — so the button just opens and closes.
export function ExpandableRow({ label, detail, chip, canEdit, children, testId }: {
  label: string
  detail?: string
  chip: ReactNode
  canEdit: boolean
  children: ReactNode
  testId?: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <RowShell
      label={label}
      detail={detail}
      testId={testId}
      right={
        <>
          {chip}
          {canEdit && (
            open ? (
              <button
                type="button"
                className="settings-row__change"
                onClick={() => setOpen(false)}
                data-testid="settings-row-done"
                data-analytics-event="project-settings-done"
              >
                <X size={13} aria-hidden /> Done
              </button>
            ) : (
              <EditButton onClick={() => setOpen(true)} />
            )
          )}
        </>
      }
      below={open && <div className="settings-row__editor">{children}</div>}
    />
  )
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v) => b.includes(v))
}

// Which polish passes run, using the same picker the new-project flow shows. Each checkbox writes the
// whole set immediately (the field is a list, and a Save button over eight checkboxes only adds a way
// to lose the edit). The clicked state is held locally until the refetched project catches up, so a
// checkbox doesn't snap back for the length of the round trip.
export function PolishPassesRow({ enabled, canEdit, patch }: {
  enabled: readonly string[]
  canEdit: boolean
  patch: (next: string[]) => Promise<PatchResult>
}) {
  const [pending, setPending] = useState<string[] | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const shown = pending ?? enabled

  useEffect(() => {
    if (pending && sameSet(pending, enabled)) setPending(null)
  }, [pending, enabled])

  async function toggle(pass: string) {
    const next = shown.includes(pass) ? shown.filter((p) => p !== pass) : [...shown, pass]
    setPending(next)
    setSaving(true)
    setError(null)
    const failure = await patch(next)
    setSaving(false)
    if (failure) {
      setPending(null)
      setError(failure)
    }
  }

  return (
    <ExpandableRow
      label="Polish passes"
      detail={shown.length ? shown.map(bugCategoryLabel).join(', ') : undefined}
      chip={<Chip tone={shown.length ? 'ok' : 'muted'}>{shown.length ? `${shown.length} enabled` : 'None'}</Chip>}
      canEdit={canEdit}
      testId="settings-row-polish-passes"
    >
      <PolishPassesSelector
        enabled={new Set(shown)}
        onToggle={(pass) => void toggle(pass)}
        disabled={saving}
      />
      {error && <ErrorNote>{error}</ErrorNote>}
    </ExpandableRow>
  )
}
